<script setup lang="ts">
// Search as you type, then pick (WAI-ARIA combobox with a listbox). Give `options`
// to filter locally, or `search` to ask the server (debounced 200 ms). Emits the
// picked option; `clearOnSelect` empties the box after a pick, for adding to a list.
import { computed, ref, useId, watch } from 'vue';
import type { ComboboxOption } from '../types.ts';


const props = withDefaults(
  defineProps<{
    label: string;
    options?: readonly ComboboxOption[];
    search?: (query: string) => Promise<readonly ComboboxOption[]>;
    placeholder?: string;
    clearOnSelect?: boolean;
    disabled?: boolean;
  }>(),
  { placeholder: 'Type to search', clearOnSelect: false, disabled: false },
);
const emit = defineEmits<{ select: [option: ComboboxOption] }>();

const id = useId();
const query = ref('');
const open = ref(false);
const active = ref(0);
const remote = ref<readonly ComboboxOption[]>([]);
let timer: ReturnType<typeof setTimeout> | undefined;

watch(query, (text) => {
  if (props.search === undefined) return;
  clearTimeout(timer);
  timer = setTimeout(() => {
    void props.search?.(text).then((found) => {
      if (query.value === text) remote.value = found;
    });
  }, 200);
});

const shown = computed(() => {
  if (props.search !== undefined) return remote.value;
  const needle = query.value.trim().toLowerCase();
  return (props.options ?? []).filter((option) => needle === '' || option.label.toLowerCase().includes(needle) || option.value.includes(needle)).slice(0, 50);
});

function pick(option: ComboboxOption | undefined): void {
  if (option === undefined || option.disabled === true) return;
  emit('select', option);
  query.value = props.clearOnSelect ? '' : option.label;
  open.value = false;
}

function onKey(event: KeyboardEvent): void {
  if (event.key === 'ArrowDown') {
    open.value = true;
    active.value = Math.min(active.value + 1, shown.value.length - 1);
  } else if (event.key === 'ArrowUp') {
    active.value = Math.max(active.value - 1, 0);
  } else if (event.key === 'Enter') {
    if (open.value) {
      event.preventDefault();
      pick(shown.value[active.value]);
    }
  } else if (event.key === 'Escape') {
    open.value = false;
  } else {
    return;
  }
  event.preventDefault();
}
</script>

<template>
  <div class="itc-combobox">
    <label class="itc-field-label" :for="`${id}-input`">{{ label }}</label>
    <input
      :id="`${id}-input`"
      v-model="query"
      class="itc-input"
      role="combobox"
      autocomplete="off"
      :placeholder="placeholder"
      :disabled="disabled"
      :aria-expanded="open && shown.length > 0"
      :aria-controls="`${id}-list`"
      :aria-activedescendant="open && shown[active] ? `${id}-option-${active}` : undefined"
      @focus="open = true"
      @input="
        open = true;
        active = 0;
      "
      @keydown="onKey"
      @blur="open = false"
    />
    <ul v-show="open && shown.length > 0" :id="`${id}-list`" class="itc-combobox__list" role="listbox" :aria-label="label">
      <li
        v-for="(option, index) in shown"
        :id="`${id}-option-${index}`"
        :key="option.value"
        role="option"
        class="itc-combobox__option"
        :class="{ 'is-active': index === active, 'is-disabled': option.disabled }"
        :aria-selected="index === active"
        :aria-disabled="option.disabled || undefined"
        @mousedown.prevent="pick(option)"
      >
        <span>{{ option.label }}</span>
        <span v-if="option.hint" class="itc-code">{{ option.hint }}</span>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.itc-combobox {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.itc-combobox__list {
  position: absolute;
  top: 100%;
  right: 0;
  left: 0;
  z-index: 50;
  max-height: 260px;
  margin: 4px 0 0;
  padding: 4px;
  overflow-y: auto;
  list-style: none;
  background: var(--itc-panel);
  border: 1px solid var(--itc-line-2);
  border-radius: var(--itc-r-control);
  box-shadow: var(--itc-shadow-pop);
}
.itc-combobox__option {
  display: flex;
  justify-content: space-between;
  gap: var(--itc-s3);
  align-items: center;
  min-height: var(--itc-tap);
  padding: 0 10px;
  border-radius: var(--itc-r-input);
  cursor: pointer;
}
.itc-combobox__option.is-active {
  background: var(--itc-panel-2);
}
.itc-combobox__option.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
